import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import { calculateCustomerGrandTotal } from '../../utils/customerFinancialUtils';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import OrderDetails from '../orders/OrderDetails';
import SupplierSessionPanel from './SupplierSessionPanel';
import { ORDER_STATUS } from '../../constants';
import {
  aggregateDealerSessionBalances,
  getDealerSessionEffectiveTotal,
  getDealerSessionRemaining,
  formatDealerBalanceLabel
} from '../../utils/dealerSessionBalanceUtils';
import { sortDealerSessionsByDate, sortSupplierSessionsByDate } from '../../utils/sessionListSortUtils';
import {
  filterDealerSessionsByDateRange,
  filterDirectPaymentsByDateRange,
  fetchOrdersForDealerSessions,
  sortOrdersForDealerPdf,
  buildSessionDateLookup,
  computeDealerAggregateSummary,
  formatDealerPdfDateRangeLabel
} from '../../utils/dealerAggregatePdfUtils';
import { generateDealerAggregatePDF, generateDealerDirectPaymentsPDF } from '../../utils/pdfGenerators';
import { buildAllDealerSummaries, buildSupplierSessionsSummary } from '../../utils/dealerListSummaryUtils';
import { aggregateSupplierSessionBalances } from '../../utils/supplierSessionBalanceUtils';
import DealerOrderSummaryCell from './DealerOrderSummaryCell';
import './DealerList.css';

// Bayi'ye özgü sipariş/kargo durumları (perakende workflow'undan bağımsız)
const DEALER_STATUS = {
  NEW_ORDER: 'new_order',
  PREPARING: 'preparing',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled'
};

const DEALER_STATUS_LABELS = {
  [DEALER_STATUS.NEW_ORDER]: 'Yeni Sipariş',
  [DEALER_STATUS.PREPARING]: 'Hazırlanıyor',
  [DEALER_STATUS.SHIPPED]: 'Kargoya Verildi',
  [DEALER_STATUS.DELIVERED]: 'Teslim Edildi',
  [DEALER_STATUS.CANCELLED]: 'İptal'
};

const DEALER_STATUS_CLASS = {
  [DEALER_STATUS.NEW_ORDER]: 'status-new',
  [DEALER_STATUS.PREPARING]: 'status-production',
  [DEALER_STATUS.SHIPPED]: 'status-ready',
  [DEALER_STATUS.DELIVERED]: 'status-completed',
  [DEALER_STATUS.CANCELLED]: 'status-cancelled'
};

// Bayi / Tedarikçi bakiye durumunu belirleyen yardımcı fonksiyon
export const getDealerBalanceStatus = (dealer, summary) => {
  if (!summary) return { hasDebt: false, hasCredit: false, isBalanced: true };

  const firmType = dealer.firmType || 'bayi';

  if (firmType === 'bayi') {
    const openBalance = parseFloat(summary.bayi?.openBalance || 0);
    const creditBalance = parseFloat(summary.bayi?.creditBalance || 0);
    const hasDebt = openBalance > 0.009;
    const hasCredit = creditBalance > 0.009 || !!summary.bayi?.isCreditor;
    const isBalanced = !hasDebt && !hasCredit;
    return { hasDebt, hasCredit, isBalanced, debtAmount: openBalance, creditAmount: creditBalance };
  }

  if (firmType === 'tedarikci') {
    const totalRemaining = parseFloat(summary.supplier?.totalRemainingTry || 0);
    const totalCredit = parseFloat(summary.supplier?.totalCreditTry || 0);
    const hasDebt = totalRemaining > 0.009;
    const hasCredit = totalCredit > 0.009 || !!summary.supplier?.isCreditor;
    const isBalanced = !hasDebt && !hasCredit;
    return { hasDebt, hasCredit, isBalanced, debtAmount: totalRemaining, creditAmount: totalCredit };
  }

  if (firmType === 'bayi_tedarikci') {
    const bayiOpen = parseFloat(summary.bayi?.openBalance || 0);
    const bayiCredit = parseFloat(summary.bayi?.creditBalance || 0);
    const supplierOpen = parseFloat(summary.supplier?.totalRemainingTry || 0);
    const supplierCredit = parseFloat(summary.supplier?.totalCreditTry || 0);

    const bayiNet = bayiOpen - bayiCredit;
    const supplierNet = summary.supplier?.totalNetTry !== undefined ? summary.supplier.totalNetTry : (supplierOpen - supplierCredit);
    const netCari = bayiNet - supplierNet;

    const hasDebt = bayiOpen > 0.009 || supplierOpen > 0.009 || netCari > 0.009;
    const hasCredit = bayiCredit > 0.009 || supplierCredit > 0.009 || netCari < -0.009;
    const isBalanced = !hasDebt && !hasCredit;
    return { hasDebt, hasCredit, isBalanced, debtAmount: Math.max(0, netCari), creditAmount: Math.max(0, -netCari) };
  }

  return { hasDebt: false, hasCredit: false, isBalanced: true };
};

const DealerList = ({ onCustomerChange }) => {
  const { user, permissions } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  
  const initialDealerState = {
    companyName: '',
    name: '',
    phone1: '',
    phone2: '',
    email: '',
    address: '',
    notes: '',
    firmType: 'bayi'
  };
  const [newDealer, setNewDealer] = useState(initialDealerState);
  const [editingDealer, setEditingDealer] = useState(null);
  const [isDealerInfoCollapsed, setIsDealerInfoCollapsed] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Tab/Filter states
  const [filterTab, setFilterTab] = useState('all'); // 'all', 'bayi', 'tedarikci', 'bayi_tedarikci'
  const [balanceFilter, setBalanceFilter] = useState('all'); // 'all', 'debt', 'balanced', 'credit'
  const [dealerSubTab, setDealerSubTab] = useState('bayi'); // 'bayi', 'tedarikci'
  
  // Supplier Sessions State
  const [supplierSessions, setSupplierSessions] = useState([]);
  const [isLoadingSupplierSessions, setIsLoadingSupplierSessions] = useState(false);

  // Döviz kurları (tedarikçi TL çevirimi için)
  const [exchangeRates, setExchangeRates] = useState({ USD: 1, EUR: 1 });
  
  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false); // OrderDetails içinde form açık mı?
  const [deleteSessionConfirm, setDeleteSessionConfirm] = useState(null); // Silinecek session
  const [sessionPage, setSessionPage] = useState(1);
  const SESSION_PAGE_SIZE = 10;
  const [dealerSearchQuery, setDealerSearchQuery] = useState('');
  const [dealerPage, setDealerPage] = useState(1);
  const DEALER_PAGE_SIZE = 10;
  const sortedSessions = useMemo(() => sortDealerSessionsByDate(sessions), [sessions]);
  const [pdfDateFrom, setPdfDateFrom] = useState('');
  const [pdfDateTo, setPdfDateTo] = useState('');
  const [directPdfDateFrom, setDirectPdfDateFrom] = useState('');
  const [directPdfDateTo, setDirectPdfDateTo] = useState('');
  const [isGeneratingDealerPdf, setIsGeneratingDealerPdf] = useState(false);
  const [dealerSummaries, setDealerSummaries] = useState({});
  const [isLoadingSummaries, setIsLoadingSummaries] = useState(false);

  // Manuel (Harici) Ödeme Accordion ve Form State'leri
  const [isDirectPaymentAccordionOpen, setIsDirectPaymentAccordionOpen] = useState(false);
  const [newDirectPaymentAmount, setNewDirectPaymentAmount] = useState('');
  const [newDirectPaymentType, setNewDirectPaymentType] = useState('Nakit');
  const [newDirectPaymentDate, setNewDirectPaymentDate] = useState(() => {
    const t = new Date();
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, '0');
    const d = String(t.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [newDirectPaymentNote, setNewDirectPaymentNote] = useState('');
  const [isAddingDirectPayment, setIsAddingDirectPayment] = useState(false);
  const [isGeneratingDirectPdf, setIsGeneratingDirectPdf] = useState(false);
  const [directPaymentPage, setDirectPaymentPage] = useState(1);
  const DIRECT_PAYMENT_PAGE_SIZE = 5;

  // Direct Payment Edit / Delete State
  const [editingDirectPayment, setEditingDirectPayment] = useState(null);
  const [editDirectPaymentAmount, setEditDirectPaymentAmount] = useState('');
  const [editDirectPaymentType, setEditDirectPaymentType] = useState('Nakit');
  const [editDirectPaymentDate, setEditDirectPaymentDate] = useState('');
  const [editDirectPaymentNote, setEditDirectPaymentNote] = useState('');
  const [deleteDirectPaymentConfirm, setDeleteDirectPaymentConfirm] = useState(null);

  // Toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast(prev => ({ ...prev, show: false })), 4000);
  }, []);

  // Döviz kuru çek (component mount'ta)
  useEffect(() => {
    fetch('https://open.er-api.com/v6/latest/USD')
      .then(r => r.json())
      .then(data => {
        if (data?.rates) {
          const usdToTry = parseFloat(data.rates.TRY) || 1;
          const eurToTry = data.rates.EUR ? parseFloat(data.rates.TRY) / parseFloat(data.rates.EUR) : 1;
          setExchangeRates({ USD: usdToTry, EUR: eurToTry });
        }
      })
      .catch(() => {});
  }, []);

  // Format Phone
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  const fetchDealers = useCallback(async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, 'dealers'));
      const snapshot = await getDocs(q);
      const dealersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Alfabetik sıralama (firma ünvanı)
      dealersData.sort((a, b) => {
        const nameA = (a.companyName || '').toLocaleLowerCase('tr-TR');
        const nameB = (b.companyName || '').toLocaleLowerCase('tr-TR');
        return nameA.localeCompare(nameB, 'tr');
      });
      
      setDealers(dealersData);
    } catch (error) {
      console.error("Bayiler yüklenirken hata:", error);
      showToast("Bayiler yüklenirken bir hata oluştu.", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  const fetchAllDealerSummaries = useCallback(async (dealerList = dealers) => {
    if (!dealerList.length) {
      setDealerSummaries({});
      return;
    }

    setIsLoadingSummaries(true);
    try {
      const [dealerSessionsSnap, supplierSessionsSnap] = await Promise.all([
        getDocs(collection(db, 'dealer_sessions')),
        getDocs(collection(db, 'supplier_sessions'))
      ]);

      const dealerSessions = dealerSessionsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      const supplierSessions = supplierSessionsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));

      setDealerSummaries(
        buildAllDealerSummaries(dealerList, dealerSessions, supplierSessions, exchangeRates)
      );
    } catch (error) {
      console.error('Bayi özetleri yüklenirken hata:', error);
    } finally {
      setIsLoadingSummaries(false);
    }
  }, [dealers, exchangeRates]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  useEffect(() => {
    if (!isLoading && dealers.length > 0) {
      fetchAllDealerSummaries(dealers);
    }
    if (!isLoading && dealers.length === 0) {
      setDealerSummaries({});
    }
  }, [dealers, isLoading, exchangeRates, fetchAllDealerSummaries]);

  // Bayi İşlemleri
  const handleAddDealer = async (e) => {
    e.preventDefault();
    if (!newDealer.companyName || !newDealer.name || !newDealer.phone1) {
      showToast('Firma Adı, Yetkili Adı ve Telefon zorunludur.', 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const dealerData = {
        ...newDealer,
        firmType: newDealer.firmType || 'bayi',
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null
      };

      const docRef = await addDoc(collection(db, 'dealers'), dealerData);
      showToast('Bayi/Tedarikçi başarıyla eklendi!', 'success');
      
      // Local state'i güncelle (okuma maliyetini sıfırlamak için)
      setDealers(prev => [{ id: docRef.id, ...dealerData }, ...prev]);
      setNewDealer(initialDealerState);
      setIsAdding(false);

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Bayi eklenirken hata:", error);
      showToast("Hata oluştu: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDealer = async (e) => {
    e.preventDefault();
    if (!editingDealer.companyName || !editingDealer.name || !editingDealer.phone1) {
      showToast('Firma Adı, Yetkili Adı ve Telefon zorunludur.', 'warning');
      return;
    }

    try {
      setIsLoading(true);
      const updateData = {
        companyName: editingDealer.companyName,
        name: editingDealer.name,
        phone1: editingDealer.phone1,
        phone2: editingDealer.phone2,
        email: editingDealer.email,
        address: editingDealer.address,
        notes: editingDealer.notes,
        firmType: editingDealer.firmType || 'bayi',
        updatedAt: new Date()
      };

      await updateDoc(doc(db, 'dealers', editingDealer.id), updateData);
      showToast('Firma bilgileri güncellendi!', 'success');
      
      // Update local state without fetching all
      setDealers(prev => prev.map(d => d.id === editingDealer.id ? { ...d, ...updateData } : d));

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Güncelleme hatası:", error);
      showToast("Hata oluştu: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDealer = async () => {
    if (!deleteConfirm) return;
    try {
      setIsLoading(true);
      await deleteDoc(doc(db, 'dealers', deleteConfirm.id));
      showToast('Bayi başarıyla silindi!', 'success');
      setDealers(prev => prev.filter(d => d.id !== deleteConfirm.id));
      setDeleteConfirm(null);
      if (editingDealer?.id === deleteConfirm.id) {
        setEditingDealer(null);
      }

      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Silme hatası:", error);
      showToast("Hata oluştu: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Sipariş Listesi (Session) Silme
  const handleDeleteSession = async () => {
    if (!deleteSessionConfirm) return;
    setIsLoadingSessions(true);
    try {
      // 1) Bu session'a ait tüm alt siparişleri bul ve sil
      const ordersQuery = query(
        collection(db, 'orders'),
        where('customerId', '==', deleteSessionConfirm.id)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const deleteOrderPromises = ordersSnapshot.docs.map(orderDoc =>
        deleteDoc(doc(db, 'orders', orderDoc.id))
      );
      await Promise.all(deleteOrderPromises);

      // 2) Session belgesini sil
      await deleteDoc(doc(db, 'dealer_sessions', deleteSessionConfirm.id));

      // 3) Local state'i güncelle
      setSessions(prev => prev.filter(s => s.id !== deleteSessionConfirm.id));
      setDeleteSessionConfirm(null);
      showToast(`Sipariş listesi ve ${ordersSnapshot.docs.length} sipariş başarıyla silindi.`, 'success');

      if (onCustomerChange) {
        onCustomerChange();
      }
      fetchAllDealerSummaries();
    } catch (error) {
      console.error('Session silme hatası:', error);
      showToast('Silme işlemi başarısız: ' + error.message, 'error');
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const fetchSupplierSessions = useCallback(async (dealerId) => {
    if (!dealerId) return;
    setIsLoadingSupplierSessions(true);
    try {
      const q = query(
        collection(db, 'supplier_sessions'),
        where('dealerId', '==', dealerId)
      );
      const snapshot = await getDocs(q);
      const sessionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const productNamesBySession = {};
      const sessionIds = sessionsData.map(session => session.id);
      for (let i = 0; i < sessionIds.length; i += 10) {
        const chunk = sessionIds.slice(i, i + 10);
        if (!chunk.length) continue;
        const ordersQuery = query(
          collection(db, 'supplier_orders'),
          where('sessionId', 'in', chunk)
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        ordersSnapshot.docs.forEach(orderDoc => {
          const data = orderDoc.data();
          if (!data.sessionId || !data.productName) return;
          if (!productNamesBySession[data.sessionId]) {
            productNamesBySession[data.sessionId] = [];
          }
          productNamesBySession[data.sessionId].push(data.productName);
        });
      }

      const enrichedSessions = sortSupplierSessionsByDate(sessionsData.map(session => ({
        ...session,
        productNames: productNamesBySession[session.id] || []
      })));

      setSupplierSessions(enrichedSessions);
    } catch (error) {
      console.error("Tedarikçi oturumları yüklenirken hata:", error);
    } finally {
      setIsLoadingSupplierSessions(false);
    }
  }, []);

  const fetchDealerSessions = useCallback(async (dealerId) => {
    if (!dealerId) return;
    setIsLoadingSessions(true);
    try {
      const q = query(
        collection(db, 'dealer_sessions'),
        where('dealerId', '==', dealerId)
      );
      const snapshot = await getDocs(q);
      const sessionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSessions(sortDealerSessionsByDate(sessionsData));
    } catch (error) {
      console.error("Müşteri oturumları yüklenirken hata:", error);
    } finally {
      setIsLoadingSessions(false);
    }
  }, []);

  const handleCreateSession = async () => {
    if (!editingDealer) return;
    try {
      setIsLoadingSessions(true);
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`; // YYYY-MM-DD (Yerel Saat Dilimine Duyarlı)
      
      const sessionData = {
        dealerId: editingDealer.id,
        dealerName: editingDealer.companyName,
        sessionDate: dateStr,
        orderDate: dateStr,
        deliveryDate: '',
        orderStatus: ORDER_STATUS.NEW_ORDER,
        paidAmount: 0,
        isPaymentCompleted: false,
        remainingAmount: 0,
        totalOrderAmount: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null
      };

      const docRef = await addDoc(collection(db, 'dealer_sessions'), sessionData);
      const newSession = { id: docRef.id, ...sessionData };
      setSessions(prev => [newSession, ...prev]);
      showToast("Yeni sipariş listesi hazır. Şimdi ürünlerinizi ekleyebilirsiniz.", "success");

      if (onCustomerChange) {
        onCustomerChange();
      }
      fetchAllDealerSummaries();
      
      // Yeni oluşturulan oturumu doğrudan aç
      const sessionNameForDetails = `${editingDealer.companyName} (${new Date().toLocaleDateString('tr-TR')})`;
      setSelectedSession({
        ...newSession,
        name: sessionNameForDetails,
        phone1: editingDealer.phone1,
        address: editingDealer.address
      });

    } catch (error) {
      console.error("Oturum oluşturulurken hata:", error);
      showToast("Sipariş listesi oluşturulamadı.", "error");
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Sipariş eklenince/silinince session bakiyesini Firestore'da güncelle
  const handleSyncSessionBalance = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      // Bu session'a ait tüm siparişleri Firestore'dan doğrudan çek (en güncel veri)
      const ordersQ = query(collection(db, 'orders'), where('customerId', '==', sessionId));
      const ordersSnap = await getDocs(ordersQ);
      const ordersData = ordersSnap.docs.map(d => d.data());

      // Session dökümanını Firestore'dan çek (vade/iskonto bilgileri için)
      const sessionRef = doc(db, 'dealer_sessions', sessionId);
      const sessionSnap = await getDocs(query(collection(db, 'dealer_sessions'), where('__name__', '==', sessionId)));
      if (sessionSnap.empty) return;
      
      const sessionFirestoreData = sessionSnap.docs[0].data();
      const currentPaid = parseFloat(sessionFirestoreData.paidAmount || 0);

      // Toplam sipariş tutarını hesapla - vade farkı ve genel iskonto dahil
      // calculateCustomerGrandTotal hiyerarşisi: Manuel Override > Vade Farkı > Genel İskonto > Ürün İndirimi > Ara Toplam
      const totalOrderAmount = calculateCustomerGrandTotal(
        { vadeliYuzde: sessionFirestoreData.vadeliYuzde, genelIskontoYuzde: sessionFirestoreData.genelIskontoYuzde },
        ordersData
      );

      // Manuel override varsa totalOrderAmount'ı override değeriyle koru, yalnızca remainingAmount güncelle
      const hasManualOverride = sessionFirestoreData.manualTotalOverride != null;
      const effectiveTotalForSession = hasManualOverride
        ? parseFloat(sessionFirestoreData.manualTotalOverride)
        : totalOrderAmount;
      const newRemaining = effectiveTotalForSession - currentPaid;

      await updateDoc(sessionRef, {
        ...(hasManualOverride ? {} : { totalOrderAmount }),
        remainingAmount: newRemaining,
        updatedAt: new Date()
      });

      // Local state'i güncelle
      setSessions(prev => prev.map(s => 
        s.id === sessionId 
          ? { ...s, ...(hasManualOverride ? {} : { totalOrderAmount: effectiveTotalForSession }), remainingAmount: newRemaining }
          : s
      ));

      // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
      if (onCustomerChange) {
        onCustomerChange();
      }
      fetchAllDealerSummaries();
      
      console.log(`Bakiye senkronize edildi: ${sessionId} - Toplam: ${totalOrderAmount}`);
    } catch (error) {
      console.error('Session bakiyesi güncellenirken hata:', error);
    }
  }, [onCustomerChange, fetchAllDealerSummaries]);

  const handleEditDealerClick = (dealer) => {
    if (editingDealer?.id === dealer.id) {
      setEditingDealer(null); // Toggle close
    } else {
      setEditingDealer(dealer);
      setIsAdding(false);
      setSessionPage(1); // Sayfa başına dön
      setPdfDateFrom('');
      setPdfDateTo('');
      
      const type = dealer.firmType || 'bayi';
      if (type === 'bayi') {
        fetchDealerSessions(dealer.id);
        setDealerSubTab('bayi');
      } else if (type === 'tedarikci') {
        fetchSupplierSessions(dealer.id);
        setDealerSubTab('tedarikci');
      } else if (type === 'bayi_tedarikci') {
        fetchDealerSessions(dealer.id);
        fetchSupplierSessions(dealer.id);
        setDealerSubTab('bayi');
      }
      
      // Scroll to top or specific element
      setTimeout(() => {
        window.scrollTo({ top: 300, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSessionPaymentSaved = useCallback((updatedInfo) => {
    if (updatedInfo?.id) {
      setSelectedSession((prev) => (prev?.id === updatedInfo.id ? { ...prev, ...updatedInfo } : prev));
      setSessions((prev) => prev.map((s) => (s.id === updatedInfo.id ? { ...s, ...updatedInfo } : s)));
    }
    if (editingDealer) {
      fetchDealerSessions(editingDealer.id);
    }
    // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
    if (onCustomerChange) {
      onCustomerChange();
    }
    fetchAllDealerSummaries();
  }, [editingDealer, fetchDealerSessions, onCustomerChange, fetchAllDealerSummaries]);

  const handleDealerUpdate = useCallback((updatedDealer) => {
    setEditingDealer(updatedDealer);
    setDealers(prev => prev.map(d => d.id === updatedDealer.id ? updatedDealer : d));
    fetchAllDealerSummaries();
  }, [fetchAllDealerSummaries]);

  const handleDealerAggregatePdf = useCallback(async (mode) => {
    if (!editingDealer || isGeneratingDealerPdf) return;

    if (mode === 'range') {
      if (!pdfDateFrom || !pdfDateTo) {
        showToast('Lütfen başlangıç ve bitiş tarihlerini seçin.', 'warning');
        return;
      }
      if (new Date(`${pdfDateFrom}T00:00:00`) > new Date(`${pdfDateTo}T00:00:00`)) {
        showToast('Başlangıç tarihi bitiş tarihinden sonra olamaz.', 'warning');
        return;
      }
    }

    const filteredSessions = mode === 'all'
      ? sessions
      : filterDealerSessionsByDateRange(sessions, pdfDateFrom, pdfDateTo);

    if (filteredSessions.length === 0) {
      showToast('Seçilen tarih aralığında sipariş listesi bulunamadı.', 'warning');
      return;
    }

    setIsGeneratingDealerPdf(true);
    try {
      const sessionIds = filteredSessions.map((session) => session.id);
      const rawOrders = await fetchOrdersForDealerSessions(sessionIds);
      const orders = sortOrdersForDealerPdf(filteredSessions, rawOrders);
      const sessionDateMap = buildSessionDateLookup(filteredSessions);
      const dealerDirectPayments = editingDealer?.directPayments || editingDealer?.paymentHistory || [];
      const filteredDirectPayments = filterDirectPaymentsByDateRange(dealerDirectPayments, pdfDateFrom, pdfDateTo, mode);
      const summary = computeDealerAggregateSummary(filteredSessions, orders, filteredDirectPayments);
      const dateRangeLabel = formatDealerPdfDateRangeLabel(pdfDateFrom, pdfDateTo, mode);

      // Tarih aralığı dışında genel (tüm zamanlar) cari durumu
      const overallSummary = computeDealerAggregateSummary(sessions, [], dealerDirectPayments);

      // Eğer firma 'bayi_tedarikci' ise tedarikçi genel cari durumunu da dahil et
      const isBayiTedarikci = editingDealer?.firmType === 'bayi_tedarikci';
      let supplierOverallSummary = null;
      if (isBayiTedarikci) {
        const supplierDirectPayments = editingDealer?.supplierDirectPayments || [];
        supplierOverallSummary = buildSupplierSessionsSummary(supplierSessions, exchangeRates, supplierDirectPayments);
      }

      generateDealerAggregatePDF({
        dealer: editingDealer,
        orders,
        summary,
        dateRangeLabel,
        sessionDateMap,
        overallSummary,
        isBayiTedarikci,
        supplierOverallSummary
      });
    } catch (error) {
      console.error('Bayi toplu PDF oluşturulamadı:', error);
      showToast('PDF oluşturulurken bir hata oluştu.', 'error');
    } finally {
      setIsGeneratingDealerPdf(false);
    }
  }, [
    editingDealer,
    exchangeRates,
    isGeneratingDealerPdf,
    pdfDateFrom,
    pdfDateTo,
    sessions,
    showToast,
    supplierSessions
  ]);

  // Harici Ödeme İşlemleri (Ekleme, Güncelleme, Silme)
  const handleAddDirectPayment = async (e) => {
    if (e) e.preventDefault();
    if (!editingDealer || !editingDealer.id) return;

    const amount = parseFloat(newDirectPaymentAmount);
    if (!amount || amount <= 0) {
      showToast('Lütfen geçerli bir ödeme tutarı girin.', 'warning');
      return;
    }
    if (!newDirectPaymentDate) {
      showToast('Lütfen ödeme tarihini girin.', 'warning');
      return;
    }

    try {
      setIsAddingDirectPayment(true);
      const existingPayments = editingDealer.directPayments || editingDealer.paymentHistory || [];
      const newPaymentRecord = {
        id: 'dp_' + Date.now().toString(),
        amount,
        type: newDirectPaymentType || 'Nakit',
        date: newDirectPaymentDate,
        notes: newDirectPaymentNote || '',
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || null
      };

      const updatedPayments = [newPaymentRecord, ...existingPayments];

      await updateDoc(doc(db, 'dealers', editingDealer.id), {
        directPayments: updatedPayments,
        updatedAt: new Date()
      });

      const updatedDealer = { ...editingDealer, directPayments: updatedPayments };
      setEditingDealer(updatedDealer);
      setDealers(prev => prev.map(d => d.id === editingDealer.id ? updatedDealer : d));

      setNewDirectPaymentAmount('');
      setNewDirectPaymentNote('');
      setDirectPaymentPage(1);

      showToast('Manuel ödeme başarıyla eklendi.', 'success');
      if (onCustomerChange) onCustomerChange();
      fetchAllDealerSummaries();
    } catch (error) {
      console.error('Manuel ödeme ekleme hatası:', error);
      showToast('Ödeme eklenirken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsAddingDirectPayment(false);
    }
  };

  const handleSaveEditedDirectPayment = async () => {
    if (!editingDealer || !editingDealer.id || !editingDirectPayment) return;

    const amount = parseFloat(editDirectPaymentAmount);
    if (!amount || amount <= 0) {
      showToast('Lütfen geçerli bir ödeme tutarı girin.', 'warning');
      return;
    }
    if (!editDirectPaymentDate) {
      showToast('Lütfen ödeme tarihini girin.', 'warning');
      return;
    }

    try {
      setIsAddingDirectPayment(true);
      const existingPayments = editingDealer.directPayments || editingDealer.paymentHistory || [];
      const updatedPayments = existingPayments.map(p => {
        if (p.id === editingDirectPayment.id) {
          return {
            ...p,
            amount,
            type: editDirectPaymentType,
            date: editDirectPaymentDate,
            notes: editDirectPaymentNote,
            updatedAt: new Date().toISOString()
          };
        }
        return p;
      });

      await updateDoc(doc(db, 'dealers', editingDealer.id), {
        directPayments: updatedPayments,
        updatedAt: new Date()
      });

      const updatedDealer = { ...editingDealer, directPayments: updatedPayments };
      setEditingDealer(updatedDealer);
      setDealers(prev => prev.map(d => d.id === editingDealer.id ? updatedDealer : d));

      setEditingDirectPayment(null);
      showToast('Ödeme bilgisi güncellendi.', 'success');
      if (onCustomerChange) onCustomerChange();
      fetchAllDealerSummaries();
    } catch (error) {
      console.error('Ödeme güncelleme hatası:', error);
      showToast('Güncelleme yapılırken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsAddingDirectPayment(false);
    }
  };

  const handleDeleteDirectPayment = async (paymentId) => {
    if (!editingDealer || !editingDealer.id || !paymentId) return;

    try {
      setIsAddingDirectPayment(true);
      const existingPayments = editingDealer.directPayments || editingDealer.paymentHistory || [];
      const updatedPayments = existingPayments.filter(p => p.id !== paymentId);

      await updateDoc(doc(db, 'dealers', editingDealer.id), {
        directPayments: updatedPayments,
        updatedAt: new Date()
      });

      const updatedDealer = { ...editingDealer, directPayments: updatedPayments };
      setEditingDealer(updatedDealer);
      setDealers(prev => prev.map(d => d.id === editingDealer.id ? updatedDealer : d));
      setDirectPaymentPage(p => Math.max(1, Math.min(p, Math.ceil((updatedPayments.length || 1) / DIRECT_PAYMENT_PAGE_SIZE))));

      setDeleteDirectPaymentConfirm(null);
      showToast('Manuel ödeme silindi.', 'success');
      if (onCustomerChange) onCustomerChange();
      fetchAllDealerSummaries();
    } catch (error) {
      console.error('Ödeme silme hatası:', error);
      showToast('Ödeme silinirken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsAddingDirectPayment(false);
    }
  };

  const handleGenerateDirectPaymentPDF = useCallback((mode) => {
    if (!editingDealer || isGeneratingDirectPdf) return;

    const allPayments = editingDealer.directPayments || editingDealer.paymentHistory || [];

    if (allPayments.length === 0) {
      showToast('Henüz kaydedilmiş harici ödeme bulunmuyor.', 'warning');
      return;
    }

    let filteredPayments = allPayments;
    let dateRangeLabel = 'Tüm Zamanlar';

    if (mode === 'range') {
      if (!directPdfDateFrom || !directPdfDateTo) {
        showToast('Lütfen harici ödemeler PDF için başlangıç ve bitiş tarihlerini seçin.', 'warning');
        return;
      }
      if (new Date(`${directPdfDateFrom}T00:00:00`) > new Date(`${directPdfDateTo}T00:00:00`)) {
        showToast('Başlangıç tarihi bitiş tarihinden sonra olamaz.', 'warning');
        return;
      }
      filteredPayments = filterDirectPaymentsByDateRange(allPayments, directPdfDateFrom, directPdfDateTo, 'range');
      dateRangeLabel = formatDealerPdfDateRangeLabel(directPdfDateFrom, directPdfDateTo, 'range');

      if (filteredPayments.length === 0) {
        showToast('Seçilen tarih aralığında kaydedilmiş harici ödeme bulunamadı.', 'warning');
        return;
      }
    }

    setIsGeneratingDirectPdf(true);
    try {
      generateDealerDirectPaymentsPDF({
        dealer: editingDealer,
        payments: filteredPayments,
        dateRangeLabel
      });
      showToast('Harici ödemeler PDF raporu oluşturuldu.', 'success');
    } catch (error) {
      console.error('Harici ödemeler PDF oluşturma hatası:', error);
      showToast('PDF oluşturulurken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsGeneratingDirectPdf(false);
    }
  }, [editingDealer, isGeneratingDirectPdf, directPdfDateFrom, directPdfDateTo, showToast]);

  const balanceCounts = useMemo(() => {
    const matchingTabDealers = dealers.filter(d => {
      if (filterTab !== 'all' && (d.firmType || 'bayi') !== filterTab) return false;
      return true;
    });

    let debt = 0;
    let balanced = 0;
    let credit = 0;

    matchingTabDealers.forEach(d => {
      const status = getDealerBalanceStatus(d, dealerSummaries[d.id]);
      if (status.hasDebt) debt++;
      if (status.isBalanced) balanced++;
      if (status.hasCredit) credit++;
    });

    return {
      all: matchingTabDealers.length,
      debt,
      balanced,
      credit
    };
  }, [dealers, filterTab, dealerSummaries]);

  const filteredDealers = useMemo(() => {
    const searchLower = dealerSearchQuery.trim().toLocaleLowerCase('tr-TR');
    const searchDigits = dealerSearchQuery.replace(/\D/g, '');

    return dealers
      .filter((d) => {
        if (filterTab !== 'all' && (d.firmType || 'bayi') !== filterTab) return false;

        // Bakiye Filtresi
        if (balanceFilter !== 'all') {
          const balanceStatus = getDealerBalanceStatus(d, dealerSummaries[d.id]);
          if (balanceFilter === 'debt' && !balanceStatus.hasDebt) return false;
          if (balanceFilter === 'balanced' && !balanceStatus.isBalanced) return false;
          if (balanceFilter === 'credit' && !balanceStatus.hasCredit) return false;
        }

        if (!searchLower) return true;

        const phoneMatch = searchDigits
          ? (d.phone1 || '').replace(/\D/g, '').includes(searchDigits)
            || (d.phone2 || '').replace(/\D/g, '').includes(searchDigits)
          : false;

        return (
          (d.companyName || '').toLocaleLowerCase('tr-TR').includes(searchLower)
          || (d.name || '').toLocaleLowerCase('tr-TR').includes(searchLower)
          || phoneMatch
          || (d.phone1 || '').includes(dealerSearchQuery.trim())
          || (d.phone2 || '').includes(dealerSearchQuery.trim())
        );
      })
      .sort((a, b) => {
        const nameA = (a.companyName || '').toLocaleLowerCase('tr-TR');
        const nameB = (b.companyName || '').toLocaleLowerCase('tr-TR');
        return nameA.localeCompare(nameB, 'tr');
      });
  }, [dealers, filterTab, balanceFilter, dealerSummaries, dealerSearchQuery]);

  const dealerTotalPages = Math.max(1, Math.ceil(filteredDealers.length / DEALER_PAGE_SIZE));

  const paginatedDealers = useMemo(
    () => filteredDealers.slice((dealerPage - 1) * DEALER_PAGE_SIZE, dealerPage * DEALER_PAGE_SIZE),
    [filteredDealers, dealerPage]
  );

  const getDebtFilterLabel = () => {
    if (filterTab === 'bayi') return 'Kalan Borcu Olan Bayiler';
    if (filterTab === 'tedarikci') return 'Borçlu Olduğumuz Tedarikçiler';
    if (filterTab === 'bayi_tedarikci') return 'Açık Bakiyesi Olanlar';
    return 'Borcu / Borcumuz Olanlar';
  };

  const getBalancedFilterLabel = () => {
    if (filterTab === 'bayi') return 'Bakiyesi Dengede';
    if (filterTab === 'tedarikci') return 'Bakiyesi Dengede';
    if (filterTab === 'bayi_tedarikci') return 'Net Cari Dengede';
    return 'Bakiyesi Dengede';
  };

  const getCreditFilterLabel = () => {
    if (filterTab === 'bayi') return 'Alacaklı Bayiler';
    if (filterTab === 'tedarikci') return 'Tedarikçi Bize Borçlu';
    if (filterTab === 'bayi_tedarikci') return 'Alacak Bakiyesi Olanlar';
    return 'Alacaklı / Fazla Ödeme';
  };

  useEffect(() => {
    setDealerPage(1);
  }, [filterTab, balanceFilter, dealerSearchQuery]);

  useEffect(() => {
    if (dealerPage > dealerTotalPages) {
      setDealerPage(dealerTotalPages);
    }
  }, [dealerPage, dealerTotalPages]);

  return (
    <div className="dealer-list-container">
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {toast.type === 'success' && <i className="fas fa-check"></i>}
              {toast.type === 'error' && <i className="fas fa-exclamation-triangle"></i>}
              {toast.type === 'warning' && <i className="fas fa-exclamation-circle"></i>}
            </div>
            <div className="toast-message">{toast.message}</div>
            <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="toast-progress"></div>
        </div>
      )}

      <div className="dealer-header">
        <h2>
          <i className="fas fa-store"></i> Toptan Bayi Yönetimi
        </h2>
        <button 
          className="add-dealer-btn"
          onClick={() => {
            setIsAdding(!isAdding);
            setEditingDealer(null);
            setNewDealer(initialDealerState);
          }}
          disabled={isLoading}
        >
          {isAdding ? (
            <><i className="fas fa-times"></i> İptal Et</>
          ) : (
            <><i className="fas fa-plus"></i> Yeni Bayi Ekle</>
          )}
        </button>
      </div>

      {isAdding && (
        <div className="add-dealer-form">
          <h3>Yeni Bayi Oluştur</h3>
          <form onSubmit={handleAddDealer}>
            <div className="dealer-info-section">
              <div className="form-grid">
                <div className="form-group">
                  <label><i className="fas fa-building"></i>Firma Adı *</label>
                  <input
                    type="text"
                    value={newDealer.companyName}
                    onChange={(e) => setNewDealer({...newDealer, companyName: e.target.value})}
                    placeholder="Bayinin Firma Unvanı"
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-user-tie"></i>Yetkili Kişi *</label>
                  <input
                    type="text"
                    value={newDealer.name}
                    onChange={(e) => setNewDealer({...newDealer, name: e.target.value})}
                    placeholder="Ad Soyad"
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-envelope"></i>E-posta</label>
                  <input
                    type="email"
                    value={newDealer.email}
                    onChange={(e) => setNewDealer({...newDealer, email: e.target.value})}
                    placeholder="ornek@firma.com"
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone"></i>Telefon 1 *</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(newDealer.phone1)}
                      onChange={(e) => setNewDealer({...newDealer, phone1: formatPhoneNumber(e.target.value)})}
                      placeholder="5XX XXX XX XX"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone-alt"></i>Telefon 2</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(newDealer.phone2)}
                      onChange={(e) => setNewDealer({...newDealer, phone2: formatPhoneNumber(e.target.value)})}
                      placeholder="5XX XXX XX XX"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label><i className="fas fa-map-marker-alt"></i>Adres</label>
                  <input
                    type="text"
                    value={newDealer.address}
                    onChange={(e) => setNewDealer({...newDealer, address: e.target.value})}
                    placeholder="Bayinin Adresi"
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-tags"></i>Firma Tipi *</label>
                  <select
                    value={newDealer.firmType || 'bayi'}
                    onChange={(e) => setNewDealer({...newDealer, firmType: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '1.05rem',
                      background: '#f8fafc',
                      color: '#1e293b'
                    }}
                  >
                    <option value="bayi">Sadece Bayi</option>
                    <option value="tedarikci">Sadece Tedarikçi</option>
                    <option value="bayi_tedarikci">Hem Bayi Hem Tedarikçi</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-btn" disabled={isLoading}>
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>} Kaydet
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Editing Dealer Form & Sessions */}
      {editingDealer && (
        <div className="add-dealer-form">
          <div
            className="section-header collapsible"
            onClick={() => setIsDealerInfoCollapsed(!isDealerInfoCollapsed)}
            style={{ marginBottom: '15px' }}
          >
            <h4>
              <i className="fas fa-building" style={{ marginRight: '10px', color: '#3b82f6' }}></i>
              Bayi Detayları & Siparişleri: {editingDealer.companyName}
            </h4>
            <i className={`fas fa-chevron-${isDealerInfoCollapsed ? 'down' : 'up'}`}></i>
          </div>

          <div className={`section-content ${isDealerInfoCollapsed ? 'collapsed' : ''}`}>
            <form onSubmit={handleUpdateDealer}>
              <div className="dealer-info-section">
              <div className="form-grid">
                <div className="form-group">
                  <label><i className="fas fa-building"></i>Firma Adı *</label>
                  <input
                    type="text"
                    value={editingDealer.companyName || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, companyName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-user-tie"></i>Yetkili Kişi *</label>
                  <input
                    type="text"
                    value={editingDealer.name || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label><i className="fas fa-envelope"></i>E-posta</label>
                  <input
                    type="email"
                    value={editingDealer.email || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, email: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone"></i>Telefon 1 *</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(editingDealer.phone1 || '')}
                      onChange={(e) => setEditingDealer({...editingDealer, phone1: formatPhoneNumber(e.target.value)})}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label><i className="fas fa-phone-alt"></i>Telefon 2</label>
                  <div className="phone-input-container">
                    <span className="phone-prefix">+90</span>
                    <input
                      type="tel"
                      className="phone-main-input"
                      value={formatPhoneNumber(editingDealer.phone2 || '')}
                      onChange={(e) => setEditingDealer({...editingDealer, phone2: formatPhoneNumber(e.target.value)})}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label><i className="fas fa-map-marker-alt"></i>Adres</label>
                  <input
                    type="text"
                    value={editingDealer.address || ''}
                    onChange={(e) => setEditingDealer({...editingDealer, address: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label><i className="fas fa-tags"></i>Firma Tipi *</label>
                  <select
                    value={editingDealer.firmType || 'bayi'}
                    onChange={(e) => setEditingDealer({...editingDealer, firmType: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '14px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      fontSize: '1.05rem',
                      background: '#f8fafc',
                      color: '#1e293b'
                    }}
                  >
                    <option value="bayi">Sadece Bayi</option>
                    <option value="tedarikci">Sadece Tedarikçi</option>
                    <option value="bayi_tedarikci">Hem Bayi Hem Tedarikçi</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="form-actions" style={{ marginTop: '10px' }}>
              <button type="submit" className="save-btn" disabled={isLoading}>
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-sync-alt"></i>} Bilgileri Güncelle
              </button>
              <button type="button" className="cancel-btn" onClick={() => setEditingDealer(null)}>
                Kapat
              </button>
            </div>
          </form>
        </div>

          {/* Tabs for bayi_tedarikci */}
          {(editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && (
            <div className="dealer-detail-tabs" style={{ display: 'flex', gap: '10px', marginTop: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>
              <button
                type="button"
                className={`detail-tab-btn ${dealerSubTab === 'bayi' ? 'active' : ''}`}
                onClick={() => setDealerSubTab('bayi')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: dealerSubTab === 'bayi' ? '#3b82f6' : '#f1f5f9',
                  color: dealerSubTab === 'bayi' ? 'white' : '#475569'
                }}
              >
                <i className="fas fa-store"></i> Bayi Siparişleri
              </button>
              <button
                type="button"
                className={`detail-tab-btn ${dealerSubTab === 'tedarikci' ? 'active' : ''}`}
                onClick={() => setDealerSubTab('tedarikci')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: dealerSubTab === 'tedarikci' ? '#8b5cf6' : '#f1f5f9',
                  color: dealerSubTab === 'tedarikci' ? 'white' : '#475569'
                }}
              >
                <i className="fas fa-truck"></i> Tedarikçi Alımları
              </button>
            </div>
          )}

          {/* Conditional Rendering of Panels */}
          {((editingDealer.firmType || 'bayi') === 'bayi' || 
            ((editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && dealerSubTab === 'bayi')) && (
            <div className="dealer-sessions-section">
              <div className="sessions-header">
                <h4>
                  <i className="fas fa-clipboard-list"></i> Sipariş Listeleri
                </h4>
                <button className="add-session-btn" onClick={handleCreateSession} disabled={isLoadingSessions}>
                  <i className="fas fa-plus-circle"></i> Yeni Sipariş
                </button>
              </div>

              {/* Genel Cari Özet Kartları */}
              {(() => {
                const directPayments = editingDealer?.directPayments || editingDealer?.paymentHistory || [];
                const supplierDirectPayments = editingDealer?.supplierDirectPayments || [];
                const {
                  grandTotal,
                  grandPaid,
                  netBalance,
                  openBalance,
                  creditBalance,
                  isCreditor,
                  isBalanced,
                  sessionPaid,
                  directPaid
                } = aggregateDealerSessionBalances(sessions, directPayments);
                const fmt = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v);

                const {
                  totalDebtTry: grandSupplierDebt,
                  totalCreditTry: grandSupplierCredit,
                  totalNetTry: grandSupplierNet
                } = aggregateSupplierSessionBalances(supplierSessions, exchangeRates, supplierDirectPayments);
                const netCariBalance = netBalance - grandSupplierNet;
                const hasAnyData = sessions.length > 0 || directPayments.length > 0;

                return (
                  <div>
                    {hasAnyData && (
                      <div className="dealer-financial-summary">
                        <div className="financial-card total-card">
                          <div className="financial-card-icon"><i className="fas fa-file-invoice-dollar"></i></div>
                          <div className="financial-card-info">
                            <span className="financial-card-label">Toplam Sipariş Tutarı</span>
                            <span className="financial-card-value">{fmt(grandTotal)}</span>
                          </div>
                        </div>
                        <div className="financial-card paid-card">
                          <div className="financial-card-icon"><i className="fas fa-check-circle"></i></div>
                          <div className="financial-card-info">
                            <span className="financial-card-label">Tahsil Edilen</span>
                            <span className="financial-card-value">{fmt(grandPaid)}</span>
                            {directPaid > 0 && (
                              <span className="financial-card-subhint" style={{ fontSize: '0.75rem', color: '#059669', opacity: 0.9 }}>
                                (Sipariş: {fmt(sessionPaid)} + Harici: {fmt(directPaid)})
                              </span>
                            )}
                          </div>
                        </div>
                        <div className={`financial-card ${isCreditor ? 'credit-card' : 'remaining-card'}`}>
                          <div className="financial-card-icon">
                            <i className={isCreditor ? 'fas fa-hand-holding-usd' : isBalanced ? 'fas fa-balance-scale' : 'fas fa-hourglass-half'}></i>
                          </div>
                          <div className="financial-card-info">
                            <span className="financial-card-label">
                              {isCreditor ? 'Alacaklı (Fazla Ödeme)' : 'Açık Bakiye'}
                            </span>
                            <span className="financial-card-value">
                              {isCreditor ? fmt(creditBalance) : fmt(openBalance)}
                            </span>
                            {isBalanced && !isCreditor && (
                              <span className="financial-card-hint">Bakiye kapandı</span>
                            )}
                            {isCreditor && (
                              <span className="financial-card-hint">Tahsilat − Sipariş tutarı</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Harici / Manuel Alınan Ödemeler Accordion */}
                    <div className="dealer-direct-payment-accordion">
                      <div 
                        className={`direct-payment-accordion-header ${isDirectPaymentAccordionOpen ? 'open' : ''}`}
                        onClick={() => setIsDirectPaymentAccordionOpen(prev => !prev)}
                      >
                        <div className="accordion-title-group">
                          <i className="fas fa-hand-holding-usd accordion-icon"></i>
                          <span className="accordion-title">Harici / Manuel Alınan Ödemeler</span>
                          <span className="accordion-badge">
                            {directPayments.length} Kayıt | Toplam: {fmt(directPaid)}
                          </span>
                        </div>
                        <div className="accordion-toggle-icon">
                          <i className={`fas ${isDirectPaymentAccordionOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                        </div>
                      </div>

                      {isDirectPaymentAccordionOpen && (
                        <div className="direct-payment-accordion-content">
                          {/* Ödeme Ekleme Formu */}
                          <form onSubmit={handleAddDirectPayment} className="direct-payment-form">
                            <div className="form-group">
                              <label>Ödeme Tutarı (₺)</label>
                              <input
                                type="number"
                                step="any"
                                placeholder="0.00"
                                value={newDirectPaymentAmount}
                                onChange={(e) => setNewDirectPaymentAmount(e.target.value)}
                                disabled={isAddingDirectPayment}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Ödeme Tipi</label>
                              <select
                                value={newDirectPaymentType}
                                onChange={(e) => setNewDirectPaymentType(e.target.value)}
                                disabled={isAddingDirectPayment}
                              >
                                <option value="Nakit">Nakit</option>
                                <option value="Kredi Kartı">Kredi Kartı</option>
                                <option value="Havale / EFT">Havale / EFT</option>
                                <option value="Çek">Çek</option>
                                <option value="Senet">Senet</option>
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Ödeme Tarihi</label>
                              <input
                                type="date"
                                value={newDirectPaymentDate}
                                onChange={(e) => setNewDirectPaymentDate(e.target.value)}
                                disabled={isAddingDirectPayment}
                                required
                              />
                            </div>
                            <div className="form-group wide">
                              <label>Açıklama / Not (Opsiyonel)</label>
                              <input
                                type="text"
                                placeholder="Örn: Cari bakiye kapatma, Banka havalesi..."
                                value={newDirectPaymentNote}
                                onChange={(e) => setNewDirectPaymentNote(e.target.value)}
                                disabled={isAddingDirectPayment}
                              />
                            </div>
                            <div className="form-group button-group direct-payment-actions-row">
                              <button
                                type="submit"
                                className="btn-add-direct-payment"
                                disabled={isAddingDirectPayment}
                              >
                                {isAddingDirectPayment ? (
                                  <i className="fas fa-spinner fa-spin"></i>
                                ) : (
                                  <>
                                    <i className="fas fa-plus-circle"></i> Ödeme Ekle
                                  </>
                                )}
                              </button>

                              <div className="direct-pdf-buttons-group">
                                <div className="direct-pdf-date-inputs">
                                  <div className="pdf-date-input-item">
                                    <label>Başlangıç</label>
                                    <input
                                      type="date"
                                      value={directPdfDateFrom}
                                      onChange={(e) => setDirectPdfDateFrom(e.target.value)}
                                      disabled={isGeneratingDirectPdf}
                                    />
                                  </div>
                                  <div className="pdf-date-input-item">
                                    <label>Bitiş</label>
                                    <input
                                      type="date"
                                      value={directPdfDateTo}
                                      onChange={(e) => setDirectPdfDateTo(e.target.value)}
                                      disabled={isGeneratingDirectPdf}
                                    />
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="btn-direct-pdf btn-direct-pdf-range"
                                  onClick={() => handleGenerateDirectPaymentPDF('range')}
                                  disabled={isGeneratingDirectPdf}
                                  title="Seçili tarih aralığındaki harici ödemeleri PDF olarak çıkar"
                                >
                                  {isGeneratingDirectPdf ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Hazırlanıyor...</>
                                  ) : (
                                    <><i className="fas fa-calendar-alt"></i> Tarih Aralığı PDF</>
                                  )}
                                </button>

                                <button
                                  type="button"
                                  className="btn-direct-pdf btn-direct-pdf-all"
                                  onClick={() => handleGenerateDirectPaymentPDF('all')}
                                  disabled={isGeneratingDirectPdf}
                                  title="Tüm harici ödemeleri kapsayan PDF raporu çıkar"
                                >
                                  {isGeneratingDirectPdf ? (
                                    <><i className="fas fa-spinner fa-spin"></i> Hazırlanıyor...</>
                                  ) : (
                                    <><i className="fas fa-file-pdf"></i> Tüm Ödemeler PDF</>
                                  )}
                                </button>
                              </div>
                            </div>
                          </form>

                          {/* Ödeme Listesi & Tablo */}
                          {directPayments.length === 0 ? (
                            <div className="direct-payments-empty">
                              <i className="fas fa-receipt"></i>
                              <p>Henüz harici/manuel ödeme eklenmemiş.</p>
                            </div>
                          ) : (
                            <>
                              <div className="direct-payments-table-wrapper">
                                <table className="direct-payments-table">
                                  <thead>
                                    <tr>
                                      <th>Ödeme Tarihi</th>
                                      <th>Ödeme Tipi</th>
                                      <th>Açıklama</th>
                                      <th>Tutar</th>
                                      <th>İşlemler</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {directPayments
                                      .slice(
                                        (directPaymentPage - 1) * DIRECT_PAYMENT_PAGE_SIZE,
                                        directPaymentPage * DIRECT_PAYMENT_PAGE_SIZE
                                      )
                                      .map((payment) => {
                                        const isEditing = editingDirectPayment?.id === payment.id;
                                        const dateFormatted = payment.date
                                          ? new Date(`${payment.date}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                                          : (payment.createdAt ? new Date(payment.createdAt).toLocaleDateString('tr-TR') : '—');

                                        if (isEditing) {
                                          return (
                                            <tr key={payment.id} className="editing-row">
                                              <td>
                                                <input
                                                  type="date"
                                                  className="edit-date-input"
                                                  value={editDirectPaymentDate}
                                                  onChange={(e) => setEditDirectPaymentDate(e.target.value)}
                                                />
                                              </td>
                                              <td>
                                                <select
                                                  className="edit-type-select"
                                                  value={editDirectPaymentType}
                                                  onChange={(e) => setEditDirectPaymentType(e.target.value)}
                                                >
                                                  <option value="Nakit">Nakit</option>
                                                  <option value="Kredi Kartı">Kredi Kartı</option>
                                                  <option value="Havale / EFT">Havale / EFT</option>
                                                  <option value="Çek">Çek</option>
                                                  <option value="Senet">Senet</option>
                                                </select>
                                              </td>
                                              <td>
                                                <input
                                                  type="text"
                                                  className="edit-note-input"
                                                  value={editDirectPaymentNote}
                                                  onChange={(e) => setEditDirectPaymentNote(e.target.value)}
                                                  placeholder="Açıklama"
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="number"
                                                  step="any"
                                                  className="edit-amount-input"
                                                  value={editDirectPaymentAmount}
                                                  onChange={(e) => setEditDirectPaymentAmount(e.target.value)}
                                                />
                                              </td>
                                              <td className="actions-cell">
                                                <button
                                                  type="button"
                                                  className="btn-save-inline"
                                                  title="Kaydet"
                                                  onClick={handleSaveEditedDirectPayment}
                                                >
                                                  <i className="fas fa-check"></i>
                                                </button>
                                                <button
                                                  type="button"
                                                  className="btn-cancel-inline"
                                                  title="İptal"
                                                  onClick={() => setEditingDirectPayment(null)}
                                                >
                                                  <i className="fas fa-times"></i>
                                                </button>
                                              </td>
                                            </tr>
                                          );
                                        }

                                        return (
                                          <tr key={payment.id}>
                                            <td data-label="Ödeme Tarihi" className="date-cell">
                                              <i className="far fa-calendar-alt"></i> {dateFormatted}
                                            </td>
                                            <td data-label="Ödeme Tipi">
                                              <span className="payment-type-pill">{payment.type || 'Nakit'}</span>
                                            </td>
                                            <td data-label="Açıklama" className="note-cell">
                                              {payment.notes || '—'}
                                            </td>
                                            <td data-label="Tutar" className="amount-cell">
                                              {fmt(payment.amount)}
                                            </td>
                                            <td data-label="İşlemler" className="actions-cell">
                                              <button
                                                type="button"
                                                className="btn-edit-payment"
                                                title="Ödeme Tarihini / Bilgilerini Düzenle"
                                                onClick={() => {
                                                  setEditingDirectPayment(payment);
                                                  setEditDirectPaymentAmount(payment.amount?.toString() || '');
                                                  setEditDirectPaymentType(payment.type || 'Nakit');
                                                  setEditDirectPaymentDate(payment.date || '');
                                                  setEditDirectPaymentNote(payment.notes || '');
                                                }}
                                              >
                                                <i className="fas fa-edit"></i>
                                              </button>
                                              <button
                                                type="button"
                                                className="btn-delete-payment"
                                                title="Ödemeyi Sil"
                                                onClick={() => setDeleteDirectPaymentConfirm(payment)}
                                              >
                                                <i className="fas fa-trash-alt"></i>
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>

                              {/* Pagination (5 satır/sayfa) */}
                              {directPayments.length > DIRECT_PAYMENT_PAGE_SIZE && (
                                <div className="direct-payments-pagination">
                                  <button
                                    type="button"
                                    className="page-btn"
                                    onClick={() => setDirectPaymentPage(p => Math.max(1, p - 1))}
                                    disabled={directPaymentPage === 1}
                                  >
                                    <i className="fas fa-chevron-left"></i>
                                  </button>

                                  {Array.from({ length: Math.ceil(directPayments.length / DIRECT_PAYMENT_PAGE_SIZE) }, (_, i) => i + 1).map(page => (
                                    <button
                                      type="button"
                                      key={page}
                                      className={`page-btn ${directPaymentPage === page ? 'active' : ''}`}
                                      onClick={() => setDirectPaymentPage(page)}
                                    >
                                      {page}
                                    </button>
                                  ))}

                                  <button
                                    type="button"
                                    className="page-btn"
                                    onClick={() => setDirectPaymentPage(p => Math.min(Math.ceil(directPayments.length / DIRECT_PAYMENT_PAGE_SIZE), p + 1))}
                                    disabled={directPaymentPage === Math.ceil(directPayments.length / DIRECT_PAYMENT_PAGE_SIZE)}
                                  >
                                    <i className="fas fa-chevron-right"></i>
                                  </button>

                                  <span className="page-info">
                                    {(directPaymentPage - 1) * DIRECT_PAYMENT_PAGE_SIZE + 1}–{Math.min(directPaymentPage * DIRECT_PAYMENT_PAGE_SIZE, directPayments.length)} / {directPayments.length} kayıt
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Net Bakiye Kartı (sadece hem bayi hem tedarikçiyse) */}
                    {(editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && (
                      <div className="net-balance-section" style={{
                        marginTop: '15px',
                        marginBottom: '20px',
                        padding: '20px',
                        background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
                        borderRadius: '16px',
                        border: '1.5px solid #d8b4fe',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.08)'
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            <i className="fas fa-balance-scale"></i> Net Cari Bakiye Durumu
                          </span>
                          <span style={{ fontSize: '0.95rem', color: '#581c87', fontWeight: 500 }}>
                            {isCreditor
                              ? `Bayi Alacağı (${fmt(creditBalance)})`
                              : `Bayinin Borcu (${fmt(openBalance)})`}
                            {grandSupplierDebt > 0 && ` − Tedarikçiye Borcumuz (${fmt(grandSupplierDebt)})`}
                            {grandSupplierCredit > 0 && ` + Tedarikçiden Alacağımız (${fmt(grandSupplierCredit)})`}
                            {grandSupplierDebt === 0 && grandSupplierCredit === 0 && ' − Tedarikçi dengede'}
                          </span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <span style={{
                            fontSize: '1.35rem',
                            fontWeight: 800,
                            color: netCariBalance > 0 ? '#166534' : netCariBalance < 0 ? '#dc2626' : '#475569',
                            fontFamily: 'Inter, monospace'
                          }}>
                            {netCariBalance > 0
                              ? `Net Alacağımız: ${fmt(netCariBalance)}`
                              : netCariBalance < 0
                                ? `Net Borcumuz: ${fmt(Math.abs(netCariBalance))}`
                                : `Dengede: ${fmt(0)}`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {isLoadingSessions ? (
                <div className="dealer-loading-container">
                  <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#94a3b8' }}></i>
                  <p style={{ color: '#64748b', marginTop: '10px' }}>Oturumlar yükleniyor...</p>
                </div>
              ) : sessions.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-box-open"></i>
                  <p>Henüz sipariş bulunmuyor. Yeni bir sipariş açarak başlayabilirsiniz.</p>
                </div>
              ) : (
                <table className="sessions-table">
                  <thead>
                    <tr>
                      <th>Sipariş Tarihi</th>
                      <th>Teslimat Tarihi</th>
                      <th>Durum</th>
                      <th>Toplam Bakiye</th>
                      <th>Ödenen / Kalan</th>
                      <th>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedSessions
                      .slice((sessionPage - 1) * SESSION_PAGE_SIZE, sessionPage * SESSION_PAGE_SIZE)
                      .map(session => {
                      const totalOrderAmt = getDealerSessionEffectiveTotal(session);
                      const paid = parseFloat(session.paidAmount || 0);
                      const remaining = getDealerSessionRemaining(session);
                      const balanceInfo = formatDealerBalanceLabel(remaining);
                      const fmtRow = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v);
                      const status = session.orderStatus || DEALER_STATUS.NEW_ORDER;
                      const statusClass = DEALER_STATUS_CLASS[status] || 'status-new';

                      // Sipariş tarihi: önce orderDate, yoksa sessionDate, yoksa createdAt
                      const displayDateStr = session.orderDate || session.sessionDate || null;
                      const sessionDisplayDate = displayDateStr
                        ? new Date(displayDateStr + 'T00:00:00')
                        : (session.createdAt?.toDate ? session.createdAt.toDate() : new Date());
                      const sessionNameForDetails = `${editingDealer.companyName} (${sessionDisplayDate.toLocaleDateString('tr-TR')})`;

                      return (
                        <tr key={session.id}>
                          <td className="session-date" data-label="Sipariş Tarihi">
                            {sessionDisplayDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </td>
                          <td className="session-date" data-label="Teslimat Tarihi" style={{ color: session.deliveryDate ? '#059669' : '#94a3b8', fontStyle: session.deliveryDate ? 'normal' : 'italic' }}>
                            {session.deliveryDate
                              ? new Date(session.deliveryDate + 'T00:00:00').toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                              : '—'}
                          </td>
                          <td data-label="Durum">
                            <select
                              className={`session-status-select ${statusClass}`}
                              value={status}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                try {
                                  await updateDoc(doc(db, 'dealer_sessions', session.id), {
                                    orderStatus: newStatus,
                                    updatedAt: new Date()
                                  });
                                  setSessions(prev => prev.map(s =>
                                    s.id === session.id ? { ...s, orderStatus: newStatus } : s
                                  ));
                                } catch (err) {
                                  console.error('Durum güncellenemedi:', err);
                                }
                              }}
                            >
                              {Object.entries(DEALER_STATUS_LABELS).map(([key, label]) => (
                                <option key={key} value={key}>{label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="finance-cell text-gray" data-label="Toplam Bakiye">
                            {fmtRow(totalOrderAmt || (Math.abs(remaining) + paid))}
                          </td>
                          <td className="finance-cell" data-label="Ödenen / Kalan">
                            <span className="text-green">{fmtRow(paid)}</span>
                            <span style={{color: '#cbd5e1', margin: '0 8px'}}>|</span>
                            {balanceInfo.type === 'credit' ? (
                              <span className="text-credit" title="Fazla ödeme — bayi alacaklı">
                                Alacaklı: {fmtRow(balanceInfo.amount)}
                              </span>
                            ) : balanceInfo.type === 'balanced' ? (
                              <span className="text-muted">Dengede</span>
                            ) : (
                              <span className="text-red">Kalan: {fmtRow(balanceInfo.amount)}</span>
                            )}
                          </td>
                          <td className="session-actions" data-label="İşlemler">
                            <button 
                              type="button"
                              className="btn-detail"
                              onClick={() => setSelectedSession({
                                ...session,
                                name: sessionNameForDetails,
                                phone1: editingDealer.phone1,
                                address: editingDealer.address
                              })}
                            >
                              <i className="fas fa-external-link-alt"></i> Siparişleri Gör
                            </button>
                            <button
                              type="button"
                              className="btn-delete-session"
                              title="Sipariş listesini ve içindeki tüm siparişleri sil"
                              onClick={() => setDeleteSessionConfirm(session)}
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Pagination */}
              {sortedSessions.length > SESSION_PAGE_SIZE && (
                <div className="sessions-pagination">
                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setSessionPage(p => Math.max(1, p - 1))}
                    disabled={sessionPage === 1}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>

                  {Array.from({ length: Math.ceil(sortedSessions.length / SESSION_PAGE_SIZE) }, (_, i) => i + 1).map(page => (
                    <button
                      type="button"
                      key={page}
                      className={`page-btn ${sessionPage === page ? 'active' : ''}`}
                      onClick={() => setSessionPage(page)}
                    >
                      {page}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="page-btn"
                    onClick={() => setSessionPage(p => Math.min(Math.ceil(sortedSessions.length / SESSION_PAGE_SIZE), p + 1))}
                    disabled={sessionPage === Math.ceil(sortedSessions.length / SESSION_PAGE_SIZE)}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>

                  <span className="page-info">
                    {(sessionPage - 1) * SESSION_PAGE_SIZE + 1}–{Math.min(sessionPage * SESSION_PAGE_SIZE, sortedSessions.length)} / {sortedSessions.length} kayıt
                  </span>
                </div>
              )}

              {sessions.length > 0 && (
                <div className="dealer-pdf-export-panel">
                  <div className="dealer-pdf-export-header">
                    <div>
                      <h5>
                        <i className="fas fa-file-pdf"></i>
                        Toplu Sipariş PDF Raporu
                      </h5>
                      <p>
                        Bu bayinin tüm sipariş listelerindeki ürünleri tek PDF&apos;de birleştirin.
                        Özet bölümünde toplam kalem, tutar ve tahsilat bilgileri yer alır.
                      </p>
                    </div>
                  </div>

                  <div className="dealer-pdf-export-controls">
                    <div className="dealer-pdf-date-range">
                      <div className="dealer-pdf-date-field">
                        <label htmlFor="dealer-pdf-from">Başlangıç</label>
                        <input
                          id="dealer-pdf-from"
                          type="date"
                          value={pdfDateFrom}
                          onChange={(e) => setPdfDateFrom(e.target.value)}
                          disabled={isGeneratingDealerPdf}
                        />
                      </div>
                      <div className="dealer-pdf-date-field">
                        <label htmlFor="dealer-pdf-to">Bitiş</label>
                        <input
                          id="dealer-pdf-to"
                          type="date"
                          value={pdfDateTo}
                          onChange={(e) => setPdfDateTo(e.target.value)}
                          disabled={isGeneratingDealerPdf}
                        />
                      </div>
                    </div>

                    <div className="dealer-pdf-export-actions">
                      <button
                        type="button"
                        className="dealer-pdf-btn dealer-pdf-btn-range"
                        onClick={() => handleDealerAggregatePdf('range')}
                        disabled={isGeneratingDealerPdf}
                      >
                        {isGeneratingDealerPdf ? (
                          <><i className="fas fa-spinner fa-spin"></i> Hazırlanıyor...</>
                        ) : (
                          <><i className="fas fa-calendar-alt"></i> Tarih Aralığı PDF</>
                        )}
                      </button>
                      <button
                        type="button"
                        className="dealer-pdf-btn dealer-pdf-btn-all"
                        onClick={() => handleDealerAggregatePdf('all')}
                        disabled={isGeneratingDealerPdf}
                      >
                        {isGeneratingDealerPdf ? (
                          <><i className="fas fa-spinner fa-spin"></i> Hazırlanıyor...</>
                        ) : (
                          <><i className="fas fa-layer-group"></i> Tüm Siparişler PDF</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {(((editingDealer.firmType || 'bayi') === 'tedarikci') || 
            ((editingDealer.firmType || 'bayi') === 'bayi_tedarikci' && dealerSubTab === 'tedarikci')) && (
            <SupplierSessionPanel
              dealer={editingDealer}
              db={db}
              user={user}
              sessions={supplierSessions}
              setSessions={setSupplierSessions}
              isLoading={isLoadingSupplierSessions}
              fetchSessions={fetchSupplierSessions}
              onCustomerChange={onCustomerChange}
              onDealerUpdate={handleDealerUpdate}
            />
          )}

          {/* Bottom Action Footer for Editing/Details view */}
          <div className="detail-footer-actions" style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '30px',
            paddingTop: '20px',
            borderTop: '2px solid #e2e8f0',
            width: '100%'
          }}>
            <button
              type="button"
              className="cancel-btn"
              onClick={() => {
                setEditingDealer(null);
                fetchAllDealerSummaries();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 36px',
                borderRadius: '12px',
                fontSize: '1.05rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
              }}
            >
              <i className="fas fa-times"></i>
              Detayları ve Siparişleri Kapat
            </button>
          </div>
        </div>
      )}


      {/* Main Dealer List */}
      {!editingDealer && !isAdding && (
        <div className="dealer-search-section">
          <div className="dealer-search-group">
            <label htmlFor="dealer-search">Arama:</label>
            <input
              id="dealer-search"
              type="text"
              placeholder="Firma ünvanı, yetkili veya telefon..."
              value={dealerSearchQuery}
              onChange={(e) => setDealerSearchQuery(e.target.value)}
              className="dealer-search-input"
            />
          </div>
        </div>
      )}

      {!editingDealer && !isAdding && (
        <div className="dealer-filter-tabs">
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'all' ? 'active' : ''}`}
            onClick={() => setFilterTab('all')}
          >
            Tümü ({dealers.length})
          </button>
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'bayi' ? 'active' : ''}`}
            onClick={() => setFilterTab('bayi')}
          >
            <i className="fas fa-store" style={{ color: '#3b82f6', marginRight: '6px' }}></i>
            Bayiler ({dealers.filter(d => (d.firmType || 'bayi') === 'bayi').length})
          </button>
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'tedarikci' ? 'active' : ''}`}
            onClick={() => setFilterTab('tedarikci')}
          >
            <i className="fas fa-truck" style={{ color: '#f97316', marginRight: '6px' }}></i>
            Tedarikçiler ({dealers.filter(d => d.firmType === 'tedarikci').length})
          </button>
          <button 
            type="button"
            className={`filter-tab-btn ${filterTab === 'bayi_tedarikci' ? 'active' : ''}`}
            onClick={() => setFilterTab('bayi_tedarikci')}
          >
            <i className="fas fa-exchange-alt" style={{ color: '#8b5cf6', marginRight: '6px' }}></i>
            Hem Bayi Hem Tedarikçi ({dealers.filter(d => d.firmType === 'bayi_tedarikci').length})
          </button>
        </div>
      )}

      {/* Bakiye Durumu Filtre Butonları (Borçlu / Dengede / Alacaklı) */}
      {!editingDealer && !isAdding && (
        <div className="dealer-balance-filter-tabs">
          <span className="balance-filter-label">
            <i className="fas fa-filter"></i> Bakiye Durumu:
          </span>
          <button
            type="button"
            className={`balance-filter-btn ${balanceFilter === 'all' ? 'active' : ''}`}
            onClick={() => setBalanceFilter('all')}
          >
            Tümü
            <span className="balance-filter-badge">{balanceCounts.all}</span>
          </button>
          <button
            type="button"
            className={`balance-filter-btn debt-btn ${balanceFilter === 'debt' ? 'active' : ''}`}
            onClick={() => setBalanceFilter('debt')}
          >
            <i className={`fas ${filterTab === 'tedarikci' ? 'fa-truck-loading' : 'fa-exclamation-circle'}`}></i>
            {getDebtFilterLabel()}
            <span className="balance-filter-badge">{balanceCounts.debt}</span>
          </button>
          <button
            type="button"
            className={`balance-filter-btn balanced-btn ${balanceFilter === 'balanced' ? 'active' : ''}`}
            onClick={() => setBalanceFilter('balanced')}
          >
            <i className="fas fa-check-circle"></i>
            {getBalancedFilterLabel()}
            <span className="balance-filter-badge">{balanceCounts.balanced}</span>
          </button>
          {balanceCounts.credit > 0 && (
            <button
              type="button"
              className={`balance-filter-btn credit-btn ${balanceFilter === 'credit' ? 'active' : ''}`}
              onClick={() => setBalanceFilter('credit')}
            >
              <i className="fas fa-hand-holding-usd"></i>
              {getCreditFilterLabel()}
              <span className="balance-filter-badge">{balanceCounts.credit}</span>
            </button>
          )}
        </div>
      )}

      <div className="dealers-table-wrapper">
        {isLoading && !isAdding && !editingDealer && dealers.length === 0 ? (
          <div className="dealer-loading-container">
            <i className="fas fa-spinner fa-spin fa-3x" style={{ color: '#4e6960' }}></i>
          </div>
        ) : filteredDealers.length === 0 ? (
          <div className="empty-state" style={{ padding: '80px 20px' }}>
            <i className="fas fa-store-slash" style={{ fontSize: '4rem', color: '#cbd5e1' }}></i>
            <h3 style={{ fontSize: '1.5rem', marginTop: '20px' }}>Eşleşen firma bulunamadı</h3>
            <p>
              {dealerSearchQuery.trim()
                ? 'Arama kriterlerine uygun bayi/tedarikçi bulunamadı.'
                : balanceFilter === 'debt'
                ? (filterTab === 'bayi'
                  ? 'Kalan borcu olan bayi bulunmamaktadır.'
                  : filterTab === 'tedarikci'
                  ? 'Borçlu olduğumuz tedarikçi bulunmamaktadır.'
                  : filterTab === 'bayi_tedarikci'
                  ? 'Açık bakiyesi olan firma bulunmamaktadır.'
                  : 'Açık borcu olan firma bulunmamaktadır.')
                : balanceFilter === 'balanced'
                ? 'Bakiyesi dengede olan firma bulunmamaktadır.'
                : balanceFilter === 'credit'
                ? 'Alacaklı/fazla ödemesi olan firma bulunmamaktadır.'
                : 'Seçilen filtreye uygun bayi/tedarikçi kaydı bulunmamaktadır.'}
            </p>
          </div>
        ) : (
          <table className="dealers-table">
            <thead>
              <tr>
                <th>Firma Ünvanı</th>
                <th>Firma Tipi</th>
                <th>Yetkili</th>
                <th>Telefon</th>
                <th>Sipariş Özeti</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDealers.map(dealer => {
                const type = dealer.firmType || 'bayi';
                return (
                  <tr key={dealer.id} className="dealer-row-item">
                    <td className="dealer-name-cell" data-label="Firma Ünvanı">{dealer.companyName}</td>
                    <td data-label="Firma Tipi">
                      <span className={`firm-type-badge ${type}`}>
                        {type === 'bayi' && <><i className="fas fa-store"></i> Bayi</>}
                        {type === 'tedarikci' && <><i className="fas fa-truck"></i> Tedarikçi</>}
                        {type === 'bayi_tedarikci' && <><i className="fas fa-exchange-alt"></i> Bayi+Tedarikçi</>}
                      </span>
                    </td>
                    <td className="dealer-company-cell" data-label="Yetkili">{dealer.name}</td>
                    <td data-label="Telefon" style={{ fontWeight: 500 }}>{dealer.phone1}</td>
                    <td data-label="Sipariş Özeti" className="dealer-summary-cell">
                      <DealerOrderSummaryCell
                        summary={dealerSummaries[dealer.id]}
                        isLoading={isLoadingSummaries}
                        firmType={type}
                      />
                    </td>
                    <td data-label="İşlemler">
                      <div className="action-buttons">
                        <button 
                          type="button"
                          className="edit-btn" 
                          title="Detay / Düzenle"
                          onClick={() => handleEditDealerClick(dealer)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button 
                          type="button"
                          className="delete-btn" 
                          title="Sil"
                          onClick={() => setDeleteConfirm(dealer)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!editingDealer && !isAdding && filteredDealers.length > 0 && (
        <div className="dealer-pagination-section">
          <div className="dealer-pagination-info">
            <span>
              {(dealerPage - 1) * DEALER_PAGE_SIZE + 1}–{Math.min(dealerPage * DEALER_PAGE_SIZE, filteredDealers.length)} / {filteredDealers.length} firma
            </span>
          </div>
          <div className="dealer-pagination-controls">
            <button
              type="button"
              onClick={() => setDealerPage((p) => Math.max(1, p - 1))}
              disabled={dealerPage === 1}
              className="dealer-pagination-btn"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {Array.from({ length: Math.min(5, dealerTotalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(dealerTotalPages - 4, dealerPage - 2)) + i;
              if (page > dealerTotalPages) return null;

              return (
                <button
                  key={page}
                  type="button"
                  onClick={() => setDealerPage(page)}
                  className={`dealer-pagination-btn ${dealerPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setDealerPage((p) => Math.min(dealerTotalPages, p + 1))}
              disabled={dealerPage === dealerTotalPages}
              className="dealer-pagination-btn"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3><i className="fas fa-exclamation-triangle" style={{ color: '#f44336', marginRight: '10px' }}></i> Bayiyi Sil</h3>
            <p><strong>{deleteConfirm.companyName}</strong> adlı bayiyi silmek istediğinizden emin misiniz?</p>
            <p className="warning-text">Bu işlem geri alınamaz.</p>
            <div className="modal-actions">
              <button 
                className="delete-confirm-btn" 
                onClick={handleDeleteDealer}
                disabled={isLoading}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : "Evet, Sil"}
              </button>
              <button 
                className="cancel-btn" 
                onClick={() => setDeleteConfirm(null)}
                disabled={isLoading}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sipariş Listesi Silme Onay Modalı */}
      {deleteSessionConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3><i className="fas fa-exclamation-triangle" style={{ color: '#f44336', marginRight: '10px' }}></i> Sipariş Listesini Sil</h3>
            <p>
              <strong>
                {deleteSessionConfirm.createdAt
                  ? new Date(deleteSessionConfirm.createdAt?.toDate ? deleteSessionConfirm.createdAt.toDate() : deleteSessionConfirm.createdAt).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                  : deleteSessionConfirm.sessionDate}
              </strong> tarihli sipariş listesini silmek istediğinizden emin misiniz?
            </p>
            <p className="warning-text">
              <i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }}></i>
              Bu liste içindeki tüm siparişler kalıcı olarak silinecektir. Bu işlem geri alınamaz.
            </p>
            <div className="modal-actions">
              <button
                className="delete-confirm-btn"
                onClick={handleDeleteSession}
                disabled={isLoadingSessions}
              >
                {isLoadingSessions ? <i className="fas fa-spinner fa-spin"></i> : 'Evet, Sil'}
              </button>
              <button
                className="cancel-btn"
                onClick={() => setDeleteSessionConfirm(null)}
                disabled={isLoadingSessions}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Harici Ödeme Silme Onay Modalı */}
      {deleteDirectPaymentConfirm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3><i className="fas fa-exclamation-triangle" style={{ color: '#f44336', marginRight: '10px' }}></i> Harici Ödemeyi Sil</h3>
            <p>
              <strong>
                {deleteDirectPaymentConfirm.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(deleteDirectPaymentConfirm.amount) : '0 ₺'}
              </strong> tutarındaki ({deleteDirectPaymentConfirm.date ? `Ödeme Tarihi: ${deleteDirectPaymentConfirm.date}` : 'Tarihsiz'}) manuel ödemeyi silmek istediğinizden emin misiniz?
            </p>
            <p className="warning-text">
              <i className="fas fa-exclamation-circle" style={{ marginRight: '6px' }}></i>
              Bu ödeme kaydı silindiğinde bakiyeden düşülecektir.
            </p>
            <div className="modal-actions">
              <button
                className="delete-confirm-btn"
                onClick={() => handleDeleteDirectPayment(deleteDirectPaymentConfirm.id)}
                disabled={isAddingDirectPayment}
              >
                {isAddingDirectPayment ? <i className="fas fa-spinner fa-spin"></i> : 'Evet, Sil'}
              </button>
              <button
                className="cancel-btn"
                onClick={() => setDeleteDirectPaymentConfirm(null)}
                disabled={isAddingDirectPayment}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ORDER DETAILS OVERLAY */}
      {selectedSession && (
        <div className="order-details-modal-overlay">
          <div className="order-details-modal-content">
            <div className="order-details-modal-header">
              <h3>
                <i className="fas fa-shopping-cart" style={{ color: 'var(--primary-600)', marginRight: '10px' }}></i>
                {selectedSession.name} - Sipariş Yönetimi
              </h3>
              <button className="close-modal-btn" onClick={async () => {
                // Modal kapanırken önce bakiyeyi senkronize et, sonra session listesini taze çek
                if (selectedSession?.id) {
                  await handleSyncSessionBalance(selectedSession.id);
                }
                if (editingDealer) {
                  fetchDealerSessions(editingDealer.id);
                }
                setSelectedSession(null);
                setIsOrderFormOpen(false);
              }}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="order-details-modal-body">
              <OrderDetails
                customerId={selectedSession.id}
                customerName={selectedSession.name}
                customerInfo={selectedSession}
                isLoading={false}
                orderStatus={selectedSession.orderStatus}
                permissions={permissions}
                isEditMode={true}
                parentCollectionName="dealer_sessions"
                onCustomerSaved={handleSessionPaymentSaved}
                onSaveVadeliSuccess={handleSessionPaymentSaved}
                onOrderSaved={() => handleSyncSessionBalance(selectedSession.id)}
                isWholesale={true}
                onFormStateChange={(isOpen) => setIsOrderFormOpen(isOpen)}
                onSessionDateChange={(sessionId, field, value) => {
                  // Sessions tablosunu güncelle
                  setSessions(prev => prev.map(s =>
                    s.id === sessionId ? { ...s, [field]: value } : s
                  ));
                  // selectedSession'u da güncelle (başlığ tarihi için)
                  setSelectedSession(prev => {
                    if (!prev || prev.id !== sessionId) return prev;
                    const updated = { ...prev, [field]: value };
                    // Başlık adını yeniden hesapla
                    if (field === 'orderDate') {
                      const d = new Date(value + 'T00:00:00');
                      updated.name = `${editingDealer.companyName} (${d.toLocaleDateString('tr-TR')})`;
                    }
                    return updated;
                  });
                  // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
                  if (onCustomerChange) {
                    onCustomerChange();
                  }
                }}
                onOrderStatusChange={async (newStatus) => {
                  try {
                    await updateDoc(doc(db, 'dealer_sessions', selectedSession.id), {
                      orderStatus: newStatus,
                      updatedAt: new Date()
                    });
                    setSelectedSession(prev => ({ ...prev, orderStatus: newStatus }));
                    setSessions(prev => prev.map(s =>
                      s.id === selectedSession.id ? { ...s, orderStatus: newStatus } : s
                    ));
                    // Dashboard önbelleğini ve istatistiklerini temizleyip yenile
                    if (onCustomerChange) {
                      onCustomerChange();
                    }
                  } catch (err) {
                    console.error('Durum güncellenemedi:', err);
                  }
                }}
              />
            </div>
            <div className="order-details-modal-footer" style={{ gap: '10px' }}>
              {isOrderFormOpen && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: '#fef3c7', border: '1px solid #f59e0b',
                  borderRadius: '8px', padding: '8px 14px',
                  color: '#92400e', fontSize: '13px', fontWeight: '500'
                }}>
                  <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                  Açık bir sipariş formu var. Önce Kaydet veya İptal'e basın.
                </div>
              )}
              <button
                className="complete-session-btn"
                disabled={isOrderFormOpen}
                title={isOrderFormOpen ? 'Önce açık sipariş formunu kaydedin veya iptal edin.' : ''}
                style={isOrderFormOpen ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
                onClick={async () => {
                  if (isOrderFormOpen) return;
                  // Önce bakiyeyi senkronize et, sonra session listesini taze çek
                  if (selectedSession?.id) {
                    await handleSyncSessionBalance(selectedSession.id);
                  }
                  if (editingDealer) {
                    fetchDealerSessions(editingDealer.id);
                  }
                  setSelectedSession(null);
                  setIsOrderFormOpen(false);
                  showToast("İşlem başarıyla tamamlandı.");
                }}>
                <i className="fas fa-check-circle"></i> İşlemi Tamamla
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DealerList;
